// Chart colour tokens.
//
// These are ZooKeep brand hues (zk-brand-guidelines) snapped to steps that
// actually work as data ink on the navy surface. The brand's main palette is
// built for light backgrounds — its blue sits at OKLCH L 0.795, well above the
// 0.48–0.67 band a dark chart surface needs, and its blue reads as near-grey at
// C 0.084. Used raw they fail on lightness and chroma both. So each token below
// keeps the brand hue ANGLE and moves lightness/chroma to a passing step.
//
// Every pair that appears together in one chart was checked with the dataviz
// validator against surface #272C47 (`--mode dark --pairs all`):
//
//   blue ↔ coral   ΔE 17.6 deutan / 28.3 normal   PASS
//   blue ↔ yellow  ΔE 21.5 protan / 24.0 normal   PASS
//
// What is deliberately NOT here: a third arbitrary hue. Blue↔purple came out at
// ΔE 13.7 for NORMAL vision — below the floor, i.e. hard to tell apart even
// with full colour vision — and coral↔yellow and coral↔green both collapse
// under deuteranopia (ΔE 4.6 and 4.4). Rather than ship a set that needs an
// apology, the charts are designed to never need three arbitrary hues at once:
// parts-of-a-whole use steps of ONE hue (the blue ramp), and everything else is
// two series or a polarity.
export const SERIES = {
  /** Primary series: booked AR, audited balance — the thing you came to read. */
  blue: '#00a1cc',
  /** Second series where two must be told apart (computed vs audited). */
  yellow: '#b18e00',
} as const

/**
 * Steps of the blue hue for parts-of-a-whole. Same hue, monotonic lightness —
 * a sequential relationship, not a categorical one, because these are two
 * components of the same quantity (money in) rather than two unrelated things.
 */
export const INFLOW_RAMP = {
  ar: '#00a1cc',
  // Two steps apart, not one. At #41c8f6 the segments were adjacent enough to
  // read as one bar with a seam in it, which defeats the point of stacking.
  fundraise: '#5fd4ff',
} as const

/**
 * Polarity, not identity. Money out and negative months are coral; money in and
 * positive months are blue. Never green/red: that pair is the worst case for
 * deuteranopia, and position (above or below the zero line) is doing the real
 * work here anyway.
 */
export const POLARITY = {
  positive: '#00a1cc',
  negative: '#e36556',
} as const

/**
 * Status, reserved. Never reused as a series colour, and never the only signal
 * — every use is accompanied by a word (invariant #3: say it out loud).
 */
export const STATUS = {
  good: '#00ad6d',
  warn: '#c3a132',
  bad: '#d5594a',
} as const

/**
 * The legend swatch for a hatched fill — the texture that marks a figure as
 * PLANNED or FORECAST rather than reported. Texture, not a third hue: the plan
 * version of a quantity is the same quantity, so it keeps the quantity's colour
 * and changes only its surface. It is also the one encoding that survives
 * greyscale printing and forced-colours, which colour alone does not.
 *
 * Mirrors the 45° SVG `<pattern>` the charts fill their plan bars with; keep
 * the two in step, or the legend stops describing the chart.
 */
export function hatchSwatch(color: string): string {
  return `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 5px)`
}

/** Recessive furniture: grid lines, axis rules, tick text. */
export const CHART_INK = {
  grid: 'rgba(226, 232, 240, 0.10)',
  axis: 'rgba(226, 232, 240, 0.28)',
  tick: '#8f9bb3',
  /** Flat ground over months the window covers but the data does not. */
  noData: 'rgba(226, 232, 240, 0.06)',
} as const
